import * as React from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { RecommendationsList } from '../../components/RecommendationsList';
import { selectRecommendations } from '../../selectors/recommendations';
import { RecommendationOfferWithHover } from '../../components/RecommendationOffer';
import { Responsive, useDeviceType } from '@cian/ui-kit';
import { EDeviceType } from '../../types/deviceType';
import { GeoInformationContainer } from '../GeoInformationContainer';
import { ContactButtonsContainer } from '../ContactButtonsContainer';
import { trackEntityTeaserShow, trackRecommendationClick } from './tracking';
import { useVisitedOffersContext } from '@cian/frontend-visited-offers-widget';
import { LabelsContainer } from '../LabelsContainer';
import { trackPageLoad } from '../../utils/analytics';
import { selectUserId } from '../../selectors/user';
import { IApplicationState } from '../../types/redux';
import { TRecommendation } from '../../types/recommendation';
import { selectPageTypeSource } from '../../selectors/pageTypeSource';
import { useApplicationContext } from '../../utils/applicationContext';

const VIEW_THRESHOLD = 0.5;
const TEASER_CALLBACK_DELAY = 1000;

interface IRecommendationViewObserverProps {
  callback(): void;
  children: React.ReactNode;
}

function RecommendationViewObserver({ callback, children }: IRecommendationViewObserverProps): React.JSX.Element {
  const elementRef = React.useRef<HTMLDivElement | null>(null);
  const callbackRef = React.useRef(callback);
  const hasTrackedRef = React.useRef(false);
  const timerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  React.useEffect(() => {
    const element = elementRef.current;

    if (!element || hasTrackedRef.current) {
      return;
    }

    const clearTrackTimer = () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const trackOnce = () => {
      if (hasTrackedRef.current) {
        return;
      }

      hasTrackedRef.current = true;
      callbackRef.current();
    };

    const scheduleTrack = () => {
      if (timerRef.current) {
        return;
      }

      timerRef.current = window.setTimeout(() => {
        trackOnce();
        timerRef.current = null;
      }, TEASER_CALLBACK_DELAY);
    };

    if (!('IntersectionObserver' in window)) {
      scheduleTrack();

      return clearTrackTimer;
    }

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];

        if (entry?.isIntersecting && entry.intersectionRatio >= VIEW_THRESHOLD) {
          scheduleTrack();

          return;
        }

        clearTrackTimer();
      },
      { threshold: VIEW_THRESHOLD },
    );

    observer.observe(element);

    return () => {
      clearTrackTimer();
      observer.disconnect();
    };
  }, []);

  return <div ref={elementRef}>{children}</div>;
}

export const RecommendationsContainer = (): React.JSX.Element | null => {
  const { telemetry } = useApplicationContext();
  const recommendations = useSelector<IApplicationState, TRecommendation[]>(selectRecommendations, shallowEqual);
  const pageTypeSource = useSelector<IApplicationState, string | null>(selectPageTypeSource);
  const device = useDeviceType();
  const userId = useSelector(selectUserId);
  const isMobile = device === EDeviceType.Phone;
  const { setOfferVisited, isOfferVisited, isLoaded } = useVisitedOffersContext();

  const handleRecommendationClick = React.useCallback(
    (offer: TRecommendation, position: number) => {
      if (pageTypeSource === 'chat_bot') {
        telemetry.increment('recommendation.offer.source.chat-bot');
      }
      setOfferVisited(offer.offerId);
      trackRecommendationClick({ offer, position, deviceType: device });
    },
    [pageTypeSource, setOfferVisited, device, telemetry],
  );

  React.useEffect(() => {
    if (isLoaded) {
      trackPageLoad({
        deviceType: device,
        userId,
        products: recommendations.map(({ offerId }: TRecommendation, index: number) => ({
          id: offerId,
          offerType: 'offer',
          position: index + 1,
          extra: isOfferVisited(offerId) ? { is_viewes: true } : undefined,
        })),
      });
    }
  }, [device, isLoaded, recommendations, userId, isOfferVisited]);

  const handleEntityTrackTeaserShow = React.useCallback((offerId: number, position: number, isMobile: boolean) => {
    trackEntityTeaserShow(offerId, position, isMobile);
  }, []);

  const recommendationsList: React.JSX.Element[] = React.useMemo(
    () =>
      recommendations.map((recOffer: TRecommendation, position: number) => (
        <div key={recOffer.offerId}>
          <RecommendationViewObserver
            callback={() => handleEntityTrackTeaserShow(recOffer.offerId, position + 1, isMobile)}
          >
            <RecommendationOfferWithHover
              position={position + 1}
              isMobile={isMobile}
              geoInformation={<GeoInformationContainer category={recOffer.category} geo={recOffer.geo} />}
              offer={recOffer}
              onClick={handleRecommendationClick}
              labels={<LabelsContainer visited={isOfferVisited(recOffer.offerId)} />}
              pageTypeSource={pageTypeSource}
            />
          </RecommendationViewObserver>
          <Responsive phone>
            <ContactButtonsContainer offerId={recOffer.offerId} position={position} />
          </Responsive>
        </div>
      )),
    [recommendations, isMobile, handleRecommendationClick, isOfferVisited, pageTypeSource, handleEntityTrackTeaserShow],
  );

  if (!recommendations.length) {
    return null;
  }

  return <RecommendationsList>{recommendationsList}</RecommendationsList>;
};
